import React, { useRef } from 'react';
import oolaWordmark from '../../../Assets/oola_w.svg';
import './DailyStreakTrackerRing.css';

let dstrkUid = 0;

/**
 * Portal V2 — reusable daily streak tracker ring.
 * Training (large) or future dashboard compact mode.
 *
 * Each segment is a short arc with a hard gap before the next —
 * no continuous track, no end-cap circles that eat the gap.
 */
export default function DailyStreakTrackerRing({
    totalActions = 3,
    completedActions = 0,
    onTap,
    disabled = false,
    size = 'training', // 'training' | 'compact'
    /**
     * training — instructional center (“Tap here”).
     * dashboard — daily-use center (“1/3” + “Actions”).
     */
    variant = 'training', // 'training' | 'dashboard'
    interactive = true,
    pulseKey = 0,
    showAmbient = true,
    /** Soft SVG bloom / segment halo. Off = crisp arcs only. */
    showGlow = true,
    /** Center Oola wordmark. Off = number-first dashboard layout. */
    showLogo = true,
    /** Finished look, but still tappable (e.g. continue CTA). */
    continueMode = false,
    /**
     * Dashboard: stay tappable after 3/3 (daily limit handled by `disabled`).
     * Step 3 training must NOT set this.
     */
    keepInteractive = false,
    label,
    subLabel,
}) {
    const uidRef = useRef(null);
    if (uidRef.current == null) {
        dstrkUid += 1;
        uidRef.current = `dstrk${dstrkUid}`;
    }
    const uid = uidRef.current;
    const done = Math.min(Math.max(0, completedActions), totalActions);
    const finished = done >= totalActions;
    const isDashboard = variant === 'dashboard';
    const canActivate =
        interactive &&
        !disabled &&
        (continueMode || keepInteractive || !finished);
    const labelText =
        label ||
        (isDashboard
            ? `${done}/${totalActions}`
            : continueMode
              ? 'Tap to continue'
              : finished && !keepInteractive
                ? 'Done'
                : 'Tap here');
    const subLabelText =
        subLabel || (isDashboard && !continueMode ? 'Actions' : null);
    const px = size === 'compact' ? 168 : 304;
    const cx = px / 2;
    const cy = px / 2;
    const stroke = size === 'compact' ? 12 : 22;
    const rTrack = size === 'compact' ? 62 : 113;

    // Tunable: how long each bar is (idle + green use the same arc).
    // 1 = full third of the ring. Lower = shorter bars / bigger gaps.
    const BAR_LENGTH = 0.78;
    const slotDeg = 360 / totalActions;
    const gapDeg = slotDeg * (1 - BAR_LENGTH);
    const segDeg = slotDeg * BAR_LENGTH;

    const fillId = `pv2-dstrk-fill-${uid}`;
    const glowId = `pv2-dstrk-glow-${uid}`;
    const glowSoftId = `pv2-dstrk-glow-soft-${uid}`;

    const polar = (angleDeg, radius) => {
        const rad = ((angleDeg - 90) * Math.PI) / 180;
        return {
            x: cx + radius * Math.cos(rad),
            y: cy + radius * Math.sin(rad),
        };
    };

    const segmentPath = (index) => {
        const start = index * slotDeg + gapDeg / 2;
        const end = start + segDeg;
        const p0 = polar(start, rTrack);
        const p1 = polar(end, rTrack);
        const large = segDeg > 180 ? 1 : 0;
        return `M ${p0.x} ${p0.y} A ${rTrack} ${rTrack} 0 ${large} 1 ${p1.x} ${p1.y}`;
    };

    const handleActivate = () => {
        if (!canActivate) return;
        onTap?.();
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleActivate();
        }
    };

    return (
        <button
            type="button"
            className={`pv2-dstrk pv2-dstrk--${size}${
                isDashboard ? ' pv2-dstrk--dashboard' : ''
            }${done > 0 ? ' is-active' : ''}${finished ? ' is-done' : ''}${
                continueMode ? ' is-continue' : ''
            }${showGlow ? '' : ' is-crisp'}${showLogo ? '' : ' is-no-logo'}`}
            onClick={handleActivate}
            onKeyDown={onKeyDown}
            disabled={!canActivate}
            aria-label={
                continueMode
                    ? 'Tap to continue'
                    : isDashboard || keepInteractive
                      ? `${done} of ${totalActions} actions completed. Mark an action step complete`
                      : finished
                        ? 'Daily streak earned'
                        : `Mark action ${done + 1} of ${totalActions} complete`
            }
        >
            {showAmbient ? (
                <span className="pv2-dstrk__ambient" aria-hidden="true">
                    <span className="pv2-dstrk__ambient-blob" />
                </span>
            ) : null}
            <svg
                className="pv2-dstrk__svg"
                width={px}
                height={px}
                viewBox={`0 0 ${px} ${px}`}
                aria-hidden="true"
            >
                <defs>
                    <linearGradient
                        id={fillId}
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                    >
                        <stop offset="0%" stopColor="#6bc4c8" />
                        <stop offset="45%" stopColor="#3cbc8e" />
                        <stop offset="100%" stopColor="#1f9a74" />
                    </linearGradient>
                    {showGlow ? (
                        <>
                            {/* Soft outer-edge bloom — haze only, no hard SourceGraphic edge */}
                            <filter
                                id={glowSoftId}
                                x="-120%"
                                y="-120%"
                                width="340%"
                                height="340%"
                            >
                                <feGaussianBlur stdDeviation="8" result="b1" />
                                <feGaussianBlur stdDeviation="18" result="b2" />
                                <feMerge>
                                    <feMergeNode in="b2" />
                                    <feMergeNode in="b1" />
                                </feMerge>
                            </filter>
                            <filter
                                id={glowId}
                                x="-80%"
                                y="-80%"
                                width="260%"
                                height="260%"
                            >
                                <feGaussianBlur stdDeviation="6" result="b" />
                                <feMerge>
                                    <feMergeNode in="b" />
                                    <feMergeNode in="SourceGraphic" />
                                </feMerge>
                            </filter>
                        </>
                    ) : null}
                </defs>

                {Array.from({ length: totalActions }).map((_, i) => {
                    const on = done > i;
                    const d = segmentPath(i);
                    return (
                        <g key={`seg-${i}`}>
                            <path
                                d={d}
                                fill="none"
                                className="pv2-dstrk__seg-idle"
                                strokeWidth={stroke}
                                strokeLinecap="round"
                            />
                            {showGlow ? (
                                <path
                                    d={d}
                                    fill="none"
                                    className={`pv2-dstrk__seg-bloom${
                                        on ? ' is-on' : ''
                                    }`}
                                    stroke="#3cbc8e"
                                    strokeWidth={stroke + 16}
                                    strokeLinecap="round"
                                    pathLength="1"
                                    filter={`url(#${glowSoftId})`}
                                />
                            ) : null}
                            <path
                                className={`pv2-dstrk__seg-fill${
                                    on ? ' is-on' : ''
                                }`}
                                d={d}
                                fill="none"
                                stroke={`url(#${fillId})`}
                                strokeWidth={stroke}
                                strokeLinecap="round"
                                pathLength="1"
                                filter={
                                    showGlow && on
                                        ? `url(#${glowId})`
                                        : undefined
                                }
                            />
                        </g>
                    );
                })}
            </svg>

            <span key={pulseKey} className="pv2-dstrk__core">
                <span className="pv2-dstrk__core-ring" aria-hidden="true" />
                <span className="pv2-dstrk__core-face">
                    {showLogo ? (
                        <img
                            src={oolaWordmark}
                            alt=""
                            className="pv2-dstrk__logo"
                            draggable={false}
                        />
                    ) : null}
                    <span className="pv2-dstrk__label-main">
                        {labelText}
                    </span>
                    {subLabelText ? (
                        <span className="pv2-dstrk__label-sub">
                            {subLabelText}
                        </span>
                    ) : null}
                </span>
            </span>
        </button>
    );
}
